import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { PasswordInput } from "@/components/PasswordInput";
import { VoucherCard } from "@/components/VoucherCard";
import { field, Notice, checkSignup } from "@/pages/Auth";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

function CodeEntry({ initial, onFound }) {
  const [code, setCode] = useState(initial || "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const check = async (c) => {
    if (!c.trim()) return setError("Please enter your gift code.");
    setBusy(true); setError("");
    try { const { data } = await api.get("/gifts/check", { params: { code: c } }); onFound(data); }
    catch (e) { setError(apiError(e)); }
    setBusy(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (initial) check(initial); }, []);
  return (
    <form onSubmit={(e) => { e.preventDefault(); check(code); }} className="glass-card p-7 sm:p-9 space-y-5" data-testid="redeem-code-form">
      <p className="eyebrow">Redeem a gift</p>
      <h1 className="font-serif text-4xl text-[#EDE6F7]" data-testid="redeem-heading">Unlock your Memories Forever gift</h1>
      <p className="text-lg text-[#B9B3C8]">Enter the code from your voucher. It looks like MF-ABCD-2345.</p>
      <input className={`${field} uppercase tracking-[0.15em]`} value={code} onChange={(e) => { setCode(e.target.value); setError(""); }} placeholder="MF-XXXX-XXXX" autoComplete="off" aria-label="Gift code" data-testid="redeem-code-input" />
      {error && <Notice tone="error" testid="redeem-code-error">{error}</Notice>}
      <button type="submit" disabled={busy} className="gold-btn w-full h-16 rounded-full text-xl disabled:opacity-60" data-testid="redeem-check-btn">{busy ? "Checking…" : "Continue"}</button>
    </form>
  );
}

function ClaimForm({ gift }) {
  const { user, login, refresh, logout } = useAuth();
  const [mode, setMode] = useState("signup");
  const [form, setForm] = useState({ name: gift.recipient_name, email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); setError(""); };
  const signup = mode === "signup";

  const redeem = async () => {
    const { data } = await api.post("/gifts/redeem", { code: gift.code });
    await refresh();
    navigate(data.unlocked.includes("base") ? "/welcome" : "/dashboard");
  };
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!user) {
      const problem = signup ? checkSignup(form) : (!form.email.trim() || !form.password ? "Please enter your email address and password." : "");
      if (problem) return setError(problem);
    }
    setBusy(true);
    try {
      if (!user) {
        const payload = { email: form.email.trim(), password: form.password };
        const { data } = await api.post(signup ? "/auth/register" : "/auth/login", signup ? { ...payload, name: form.name.trim(), confirm_password: form.confirm } : payload);
        if (data.mfa) { setBusy(false); return setError("Administrator accounts can't redeem gift codes. Please use a customer account."); }
        login(data.token, data.user);
      }
      await redeem();
    } catch (err) { setError(apiError(err)); setBusy(false); }
  };

  const ownsAll = user && gift.items.every((i) => (i === "base" ? user.paid : user[i]));
  if (ownsAll) return (
    <div className="glass-card p-7 sm:p-9 space-y-4" data-testid="redeem-already-paid">
      <h2 className="font-serif text-3xl text-[#EDE6F7]">Your account already has this gift</h2>
      <p className="text-[#B9B3C8]">You're logged in as {user.email}, which already has everything this gift includes. The code hasn't been used, so you could pass it on to someone else.</p>
      <div className="flex flex-wrap gap-4"><Link to="/dashboard" className="gold-btn inline-flex items-center h-14 px-7 rounded-full" data-testid="redeem-go-dashboard">Go to your family</Link>
        <button type="button" onClick={logout} className="text-[#C9C2D8] underline underline-offset-4" data-testid="redeem-logout">Log out to use another account</button></div>
    </div>
  );
  return (
    <form onSubmit={submit} noValidate className="glass-card p-7 sm:p-9 space-y-5" data-testid="redeem-claim-form">
      <h2 className="font-serif text-3xl text-[#EDE6F7]" data-testid="redeem-claim-heading">{user ? "Unlock it on your account" : signup ? "Create your account to unlock it" : "Log in to unlock it"}</h2>
      {user ? <p className="text-[#B9B3C8]">You're logged in as <span className="text-[#EDE6F7]">{user.email}</span>. We'll add anything from this gift your account doesn't already have. There's nothing to pay.</p> : <>
        {signup && <div><label htmlFor="redeem-name" className="eyebrow block mb-2">Your name</label><input id="redeem-name" className={field} value={form.name} onChange={set("name")} autoComplete="name" data-testid="redeem-name" /></div>}
        <div><label htmlFor="redeem-email" className="eyebrow block mb-2">Email address</label><input id="redeem-email" type="email" className={field} value={form.email} onChange={set("email")} autoComplete={signup ? "email" : "username"} placeholder="you@example.com" data-testid="redeem-email" /></div>
        <PasswordInput label="Password" name="password" testid="redeem-password" value={form.password} onChange={set("password")} autoComplete={signup ? "new-password" : "current-password"} error={!!error} />
        {signup && <p className="-mt-3 text-sm text-[#8C84A0]">At least 8 characters, including a letter and a number.</p>}
        {signup && <PasswordInput label="Confirm password" name="confirm_password" testid="redeem-confirm" value={form.confirm} onChange={set("confirm")} autoComplete="new-password" />}
      </>}
      {error && <Notice tone="error" testid="redeem-error">{error}</Notice>}
      <button type="submit" disabled={busy} className="gold-btn w-full min-h-16 py-3 rounded-full text-lg sm:text-xl disabled:opacity-60" data-testid="redeem-submit">
        {busy ? "Unlocking…" : user ? "Unlock my gift" : signup ? "Create account & unlock my gift" : "Log in & unlock my gift"}
      </button>
      {!user && <button type="button" onClick={() => { setMode(signup ? "login" : "signup"); setError(""); }} className="text-[#C9C2D8] underline underline-offset-4" data-testid="redeem-toggle">
        {signup ? "Already have an account? Log in" : "New here? Create an account"}</button>}
    </form>
  );
}

export default function Redeem() {
  const [params] = useSearchParams();
  const { ready } = useAuth();
  const [gift, setGift] = useState(null);
  if (!ready) return <div className="min-h-screen legacy-bg" />;
  return (
    <div className="min-h-screen legacy-bg">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 grid lg:grid-cols-2 gap-8 items-start fade-up">
        {gift ? <ClaimForm gift={gift} /> : <CodeEntry initial={params.get("code")} onFound={setGift} />}
        {gift ? (
          <div className="space-y-3" data-testid="redeem-gift-found">
            <VoucherCard recipient={gift.recipient_name} buyer={gift.buyer_name} message={gift.message} code={gift.code} validUntil={gift.valid_until} includes={gift.includes} testid="redeem-voucher" />
            <button type="button" onClick={() => setGift(null)} className="text-sm text-[#C9C2D8] underline underline-offset-4" data-testid="redeem-different-code">Use a different code</button>
          </div>
        ) : (
          <div className="glass-card p-7 sm:p-9 space-y-3 text-[#B9B3C8]" data-testid="redeem-info">
            <p className="eyebrow">What's included</p>
            <p>Your Memories Forever keepsake: you as the storyteller, with your family's voice memories joined into one audio keepsake you can download and keep. Your voucher shows exactly what's included.</p>
            <p>There's nothing to pay and no subscription.</p>
          </div>
        )}
      </main>
    </div>
  );
}
